import { useState, useEffect } from "react";
import { ArrowReloadHorizontalIcon, InformationIcon } from "src/assets/icons";
import {
  BACKGROUND_LIGHT,
  QUATERNARY_GREY,
  SUCCESS_DARK_BACKGROUND,
} from "src/constants/colors";
import { useApplication } from "./ApplicationContext";
import styled from "@emotion/styled";
import { GhostButton } from "src/components/Buttons";
import { useDealer } from "src/hooks/useDealer";
import { useSafeApiCall } from "src/hooks/api/useSafeApiCall";
import { useApiClient } from "src/hooks/api/useApiClient";
import { API_PREFIX_PATH, APPLICATION_STATUS_KEYS } from "src/constants";

const BannerContainer = styled.div<{ showBanner: boolean }>`
  display: flex;
  padding: ${({ showBanner }) => (showBanner ? "4px 16px" : "0 16px")};
  justify-content: space-between;
  align-items: center;
  border-radius: 32px;
  background: ${SUCCESS_DARK_BACKGROUND};
  margin-top: ${({ showBanner }) => (showBanner ? "24px" : "0")};
  color: ${BACKGROUND_LIGHT};
  font-size: 14px;
  opacity: ${({ showBanner }) => (showBanner ? 1 : 0)};
  max-height: ${({ showBanner }) => (showBanner ? "100px" : "0")};
  overflow: hidden;
  pointer-events: ${({ showBanner }) => (showBanner ? "auto" : "none")};
  transition: all 0.3s ease-in-out;
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResendInvitationButton = styled(GhostButton)`
  display: flex;
  padding: 8px 10px 8px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  width: 131px;
  color: ${BACKGROUND_LIGHT};
  box-sizing: border-box;
  min-height: 32px;

  &:hover {
    background: ${QUATERNARY_GREY};
    color: ${BACKGROUND_LIGHT};
  }
`;

export default () => {
  const { dealer_id } = useDealer();
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();
  const {
    application: { application_status, borrower, application_id },
  } = useApplication();
  const [showBanner, setShowBanner] = useState(true);
  const [loading, setLoading] = useState(false);
  const [shouldHideBanner, setShouldHideBanner] = useState(false);

  useEffect(() => {
    if (!loading && shouldHideBanner) {
      setShowBanner(false);
      setShouldHideBanner(false);

      setTimeout(() => {
        setShowBanner(true);
      }, 180000);
    }
  }, [loading, shouldHideBanner]);

  const handleResendInvite = () => {
    const path = `${API_PREFIX_PATH}/${dealer_id}/applications/invite/${application_id}/resend`;
    const successMsg = `We have resent an invitation to ${borrower?.email ? borrower?.email : borrower?.phone_number}`;

    setLoading(true);

    void safeApiCall(
      () =>
        fetchFromApi(path, {
          method: "POST",
        }).then(() => {
          setLoading(false);
          setShouldHideBanner(true);
        }),
      {
        toastSuccessMsg: successMsg,
        toastErrorMsg: "Invitation failed to send",
        retryFn: () => handleResendInvite(),
        suppressErrorRedirect: true,
      },
    );
  };

  if (application_status !== APPLICATION_STATUS_KEYS.Invited) return null;

  return (
    <BannerContainer showBanner={showBanner}>
      <LeftContainer>
        <InformationIcon
          fill={BACKGROUND_LIGHT}
          characterFill={SUCCESS_DARK_BACKGROUND}
          opacity={1}
        />
        <span>
          An invitation was sent to {borrower?.first_name} and is still pending.
        </span>
      </LeftContainer>
      <ResendInvitationButton onClick={handleResendInvite} loading={loading}>
        <span>Resend Invite</span>
        <ArrowReloadHorizontalIcon />
      </ResendInvitationButton>
    </BannerContainer>
  );
};
